import React from 'react';
import { 
  ThemeProvider, 
  CssBaseline, 
  Box, 
  Typography, 
  Button,
  Paper
} from '@mui/material';
import { Link } from 'react-router-dom';
import { Launch } from '@mui/icons-material';
import theme from './theme';
import SideNav from './SideNav';

export default function HomePage() {
  // const navigate = useNavigate();

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: 'flex' }}>
        {/* Navigation Sidebar */}
        <SideNav paperBg={'background.paper'} titleColor={'primary.main'} />

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 4,
            // width: `calc(100% - ${drawerWidth}px )`
          }}
        >
          <Typography variant="h1" sx={{ mb: 3, fontWeight: 700 }}>
            Polyguard Developer Portal
          </Typography>
          
          <Typography variant="h5" sx={{ mb: 4, color: 'text.secondary', lineHeight: 1.6 }}>
            Build secure identity verification into your applications with our comprehensive SDK and APIs.
          </Typography>

          {/* Prominent Sandbox Link */}
          <Paper 
            elevation={2} 
            sx={{ 
              p: 4, 
              mb: 4, 
              background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              color: 'white'
            }}
          >
            <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
              Try the Sandbox
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
              Test Polyguard identity integrations in our interactive playground. 
              Configure proof types, test different integration methods, and see live code examples.
            </Typography>
            <Button
              variant="contained"
              size="large"
              component={Link}
              to="/sandbox"
              endIcon={<Launch />}
              sx={{
                backgroundColor: 'white',
                color: 'primary.main',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: '#f5f5f5'
                }
              }}
            >
              Open Sandbox
            </Button>
          </Paper>

          {/* Quick Links Grid */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 3, mb: 4 }}>
            <Paper elevation={1} sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                📚 Documentation
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Complete guides, API references, and integration examples.
              </Typography>
              <Button 
                variant="outlined" 
                component={Link} 
                to="/documentation"
                sx={{ fontWeight: 600 }}
              >
                View Docs
              </Button>
            </Paper>


            <Paper elevation={1} sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                🛠️ Live Testing
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Interactive sandbox for testing integrations and configurations.
              </Typography>
              <Button 
                variant="outlined" 
                component={Link} 
                to="/sandbox"
                sx={{ fontWeight: 600 }}
              >
                Try Now
              </Button>
            </Paper>
          </Box>

          {/* Getting Started Section */}
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
            Getting Started
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
            The Polyguard SDK provides secure, privacy-preserving identity verification for your applications. 
            Our platform supports multiple integration patterns including WebSockets, redirects, webhooks, and cookies.
          </Typography>

          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
            Choose from various proof types including full name verification, geographic region, 
            phone number verification, age confirmation, and employer verification to meet your specific requirements.
          </Typography>

          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Next Steps:
            </Typography>
            <Box component="ol" sx={{ pl: 2 }}>
              <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                Explore the <Link to="/sandbox" style={{ color: theme.palette.primary.main, textDecoration: 'none', fontWeight: 600 }}>Sandbox</Link> to test integrations
              </Typography>
              <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                Review the <Link to="/documentation" style={{ color: theme.palette.primary.main, textDecoration: 'none', fontWeight: 600 }}>Documentation</Link> for detailed guides
              </Typography>
              <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                Install the SDK using our <Link to="/imports" style={{ color: theme.palette.primary.main, textDecoration: 'none', fontWeight: 600 }}>Import instructions</Link>
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
} 